import { useEffect, useMemo } from 'react';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ExternalLink, ImageOff, LayoutGrid, Plus } from 'lucide-react';
import { api } from '../../lib/api';
import { isLiveTicketEvent, type LiveEvent } from '@vd/types';
import styles from './CommittedTicketSet.module.css';

/**
 * The tickets a walkthrough produced, shown as the work they are (TOADO-305).
 *
 * This is the payoff moment of the whole recorder: someone narrates a screen
 * and the system turns it into N filed tickets. Before this it ended by
 * printing the ticket ids, which is the system's own bookkeeping rather than
 * anything the person can act on or feel good about.
 */

/** A fetch that found the ticket gone: deleted, or moved out of reach. */
const isGone = (error: unknown) => (error as { status?: number } | null)?.status === 404;

/** A ticket's page as a link target: an http(s) address only, never a script or a file. */
export function webAddress(url: string | null | undefined): string | undefined {
  if (!url) return undefined;
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:' ? parsed.href : undefined;
  } catch {
    return undefined;
  }
}

export interface CommittedTicketSetProps {
  ticketIds: string[];
  /**
   * Draft titles and thumbnails from the walkthrough segments, keyed by ticket
   * id. Rendered immediately so the set is never empty while the real tickets
   * load, and so it still says something useful if a fetch fails.
   */
  fallbackByTicketId: Record<string, { title: string; thumbnailUrl?: string }>;
  /** Where "back to the board" goes. Omitted when the project is unknown. */
  projectId?: string;
}

export function CommittedTicketSet({
  ticketIds,
  fallbackByTicketId,
  projectId,
}: CommittedTicketSetProps) {
  const queryClient = useQueryClient();

  const results = useQueries({
    queries: ticketIds.map((ticketId) => ({
      queryKey: ['ticket', ticketId],
      queryFn: () => api.getTicket(ticketId),
      // The AI title worker writes back after commit, so a ticket fetched the
      // instant it was created often carries the placeholder. The live
      // subscription below is what replaces it; this is only the first read.
      staleTime: 10_000,
    })),
  });

  // The title-generation worker emits `ticket.updated` when it writes the AI
  // title back. Without this the set shows "Untitled capture" until a manual
  // refresh, on exactly the screen where someone is admiring what they made.
  useEffect(() => {
    if (ticketIds.length === 0) return;
    const owned = ticketIds;
    return api.subscribe((event: LiveEvent) => {
      // Match per id rather than reading `event.ticketId` directly: not every
      // LiveEvent variant carries one, and the guard is what narrows the union.
      const hit = owned.find((ticketId) => isLiveTicketEvent(event, ticketId));
      if (!hit) return;
      void queryClient.invalidateQueries({ queryKey: ['ticket', hit] });
    });
  }, [queryClient, ticketIds]);

  const tickets = useMemo(() => ticketIds.flatMap((ticketId, index) => {
    // A ticket deleted since, from its editor below or anywhere else, is no
    // longer one this walkthrough filed (Jason, 11 Oct).
    if (isGone(results[index]?.error)) return [];
    const fetched = results[index]?.data;
    const fallback = fallbackByTicketId[ticketId];
    return [{
      id: ticketId,
      // A pending AI title is shown as pending rather than as the raw
      // placeholder string, which reads as a bug to anyone who has not seen it.
      titlePending: fetched?.titlePending === true,
      title: fetched?.titlePending
        ? ''
        : fetched?.title || fallback?.title || '',
      thumbnailUrl: fetched?.thumbnailUrl || fallback?.thumbnailUrl,
      projectId: fetched?.projectId,
      sourceUrl: fetched?.sourceUrl ?? null,
    }];
  }), [fallbackByTicketId, results, ticketIds]);

  if (ticketIds.length === 0) return null;

  const boardProjectId = projectId ?? tickets.find((ticket) => ticket.projectId)?.projectId;
  // Back to the page the walkthrough ended on, to record again there (Jason,
  // 11 Oct): the last ticket's page, else the recordings page.
  const recordedOn = [...tickets].reverse().map((ticket) => webAddress(ticket.sourceUrl)).find(Boolean);

  return (
    <section className={styles.panel} aria-label="Tickets created from this walkthrough">
      <header className={styles.head}>
        <div>
          <h2>
            {tickets.length} ticket{tickets.length === 1 ? '' : 's'} filed
          </h2>
          <p>Each keeps its transcript and snapshots, and its clip for as long as walkthrough video is kept.</p>
        </div>
        <div className={styles.actions}>
          {boardProjectId && (
            <Link className={styles.primaryAction} to={`/projects/${boardProjectId}`}>
              <LayoutGrid size={14} /> Open the board
            </Link>
          )}
          {recordedOn ? (
            <a className={styles.secondaryAction} href={recordedOn}>
              <Plus size={14} /> Record another
            </a>
          ) : (
            <Link className={styles.secondaryAction} to="/recordings">
              <Plus size={14} /> Record another
            </Link>
          )}
        </div>
      </header>

      <ul className={styles.grid}>
        {tickets.map((ticket) => (
          <li key={ticket.id}>
            <Link className={styles.card} to={`/tickets/${ticket.id}`}>
              <span className={styles.shot}>
                {ticket.thumbnailUrl
                  ? <img src={ticket.thumbnailUrl} alt="" loading="lazy" />
                  : <span className={styles.shotEmpty}><ImageOff size={17} /></span>}
              </span>
              <span className={styles.body}>
                {ticket.titlePending
                  ? <span className={styles.pendingTitle}>Writing a title…</span>
                  : <strong>{ticket.title || 'Untitled walkthrough moment'}</strong>}
                <span className={styles.ticketId}>{ticket.id} <ExternalLink size={11} /></span>
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
